import React, { useContext } from "react";
import { Building2, Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useMyTenants } from "@/entities/session/model/useSessionTenants";
import { useSwitchTenant } from "@/features/tenant-session/model/useSwitchTenant";
import { authContext } from "@/shared/model/authContext";
import { ROUTES } from "@/shared/config/routes";
import { tenantHome } from "@/shared/lib/router/tenantPath";
import { getApiErrorMessage } from "@/shared/lib/errorMessage";
import { useToast } from "@/shared/ui/toast/ToastContext";
import { DropdownItem, DropdownMenu } from "@/shared/ui/kit/DropdownMenu";

/** Seletor de organização: só aparece para quem pertence a mais de uma. */
export function TenantSwitcher({ expanded }: { expanded: boolean }) {
    const { t } = useTranslation("common");
    const toast = useToast();
    const { isSupportSession } = useContext(authContext);
    // Numa sessão de suporte a troca é recusada pelo backend.
    const { data: tenants = [] } = useMyTenants(!isSupportSession);
    const switchTenant = useSwitchTenant();
    // Em caso de sucesso a página recarrega: o spinner fica até lá.
    const switchingTo = switchTenant.isPending || switchTenant.isSuccess ? switchTenant.variables?.tenantId ?? null : null;

    if (tenants.length < 2) return null;

    const current = tenants.find((tenant) => tenant.current);

    function handleSelect(tenantId: string, slug: string | null | undefined) {
        switchTenant.mutate(
            { tenantId, destination: slug ? tenantHome(slug) : ROUTES.home },
            { onError: (error) => toast.error(getApiErrorMessage(error, t("tenantSwitcher.error"))) },
        );
    }

    return (
        <DropdownMenu
            align="start"
            className="min-w-[14rem]"
            trigger={
                <button
                    type="button"
                    aria-label={t("tenantSwitcher.label")}
                    title={current?.name}
                    className="flex w-full items-center gap-2 rounded-md bg-black/20 px-3 py-2 text-left text-white hover:bg-black/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav_accent group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-2"
                >
                    {switchingTo ? <Loader2 size={16} className="shrink-0 animate-spin" /> : <Building2 size={16} className="shrink-0" />}
                    {expanded && (
                        <>
                            <span className="min-w-0 flex-1 truncate text-sm font-semibold">{current?.name ?? t("tenantSwitcher.label")}</span>
                            <ChevronsUpDown size={14} className="shrink-0 text-white/60" />
                        </>
                    )}
                </button>
            }
        >
            {tenants.map((tenant) => (
                <DropdownItem
                    key={tenant.id}
                    disabled={tenant.current || switchingTo !== null}
                    onSelect={() => handleSelect(tenant.id, tenant.slug)}
                    icon={tenant.current ? <Check /> : <Building2 />}
                >
                    <span className="truncate">{tenant.name}</span>
                </DropdownItem>
            ))}
        </DropdownMenu>
    );
}
